import { NavPromptMessageProps } from '@deltek/specification-client/core/Forms/NavPrompt'
import NavPromptDialogContainer from '@deltek/specification-client/core/Forms/NavPromptDialogContainer'
import GridContainer from '@deltek/specification-client/core/Surfaces/GridContainer'
import GridItem from '@deltek/specification-client/core/Surfaces/GridItem'
import { pxToRem } from '@deltek/specification-client/core/Utilities/unitUtils'
import { DragOverEvent } from '@dnd-kit/core'
import {
    restrictToParentElement,
    restrictToVerticalAxis,
} from '@dnd-kit/modifiers'
import { arrayMove, verticalListSortingStrategy } from '@dnd-kit/sortable'
import { makeStyles, Theme, Typography } from '@material-ui/core'
import { CheckOutlined } from '@material-ui/icons'
import React, {
    FC,
    memo,
    MouseEventHandler,
    useCallback,
    useEffect,
    useMemo,
    useState,
} from 'react'

import SortableComponent from 'components/sortable/SortableComponent'
import SortableItem from 'components/sortable/SortableItem'
import colors from 'pages/ProductsPage/ProductDetailsPage/listingColors'

import DeleteButton from '../../components/DeleteButton'
import {
    ERROR_DUPLICATE_HEADER,
    PROMPT_DISCARD_CHANGES,
    TITLE_DISCARD_CHANGES,
} from '../../constants'
import { PPL_ACTIONS, usePPLContext } from '../../PPLProvider'
import { checkAllDeleteableOptions } from '../../pplUtils'

import clsx from 'clsx'
import PropertyNameAutoComplete from '../../components/PropertyNameAutoComplete'
import { NonMSPropertyType, NonMSSubPropertyType } from '../nonMSTypes'
import NonMSEditableSubProperty from './NonMSEditableSubProperty'

const {
    boxBorder,
    boxBackground,
    defaultLight,
    inputBorder,
    errorColor,
    checkButton,
    productDetailsText,
    buttonSecondary,
} = colors

const useStyles = makeStyles((theme: Theme) => ({
    nonMSBasicContainer: {
        width: 'inherit',
        padding: theme.spacing(1),
    },
    propertyNameInput: {
        width: '100%',
        border: `1px solid ${inputBorder}`,
        padding: theme.spacing(1),
        paddingBottom: theme.spacing(0.75),
        fontWeight: 500,
        marginBottom: theme.spacing(1.5),
        '&::placeholder': {
            color: productDetailsText,
            fontWeight: 500,
        },
        '&.error': {
            border: `1px solid ${errorColor}`,
            outline: 'none',
            marginBottom: theme.spacing(2),
        },
        '&:focus': {
            outline: 'none',
            borderBottom: `1px solid ${boxBorder}`,
        },
        '&:-internal-autofill-selected': {
            backgroundColor: `${defaultLight} !important`,
        },
    },
    subPropertyContainer: {
        backgroundColor: defaultLight,
        marginBottom: theme.spacing(0.7),
        paddingTop: theme.spacing(0.5),
    },
    basicPropsContainer: {
        width: '100%',
    },
    tab2Container: {
        display: 'flex',
        flexWrap: 'wrap',
        justifyContent: 'center',
        backgroundColor: boxBackground,
        border: `1px solid ${boxBorder}`,
        borderRadius: theme.spacing(1),
        width: '100%',
        maxWidth: pxToRem(705),
        marginBottom: theme.spacing(0.5),
    },
    cancelAdd: {
        '&:hover': {
            cursor: 'pointer',
        },
    },
    propertyNameInputContainer: {
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        marginBottom: theme.spacing(1),
    },
    errorLabel: {
        position: 'absolute',
        color: errorColor,
        height: '8px',
        fontSize: '10px',
        left: theme.spacing(1),
        top: theme.spacing(4.5),
    },
    actionContainer: {
        marginLeft: theme.spacing(0.5),
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        flexDirection: 'column',
    },
    checkContainer: {
        height: pxToRem(36),
        width: pxToRem(36),
        backgroundColor: checkButton,
        borderRadius: pxToRem(36),
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        color: defaultLight,
        marginBottom: theme.spacing(0.7),
        '&.saveDisabled': {
            backgroundColor: boxBorder,
            borderColor: boxBorder,
        },
    },
}))

interface NonMSProps {
    property?: NonMSPropertyType
}

const NonMSEditableProperty: FC<NonMSProps> = ({ property }) => {
    const classes = useStyles()

    const { nonMSProperties: state, dispatch } = usePPLContext()

    const [processInsert, setProcessInsert] = useState(false)

    const [propertyName, setPropertyName] = useState(
        state.editableProperty?.propertyName ?? ''
    )

    const properties = useMemo(() => state.properties, [state.properties])

    const subProperties: Array<NonMSSubPropertyType> = useMemo(
        () => state.editableProperty?.subProperties ?? [],
        [state.editableProperty]
    )

    const handleDragEnd = useCallback(
        (drag: DragOverEvent) => {
            const { active, over } = drag
            if (!!active && !!over && active.id !== over.id) {
                const oldItem = subProperties.findIndex(
                    (item: NonMSSubPropertyType) =>
                        item.propertyId === active.id
                )
                const newItem = subProperties.findIndex(
                    (item: NonMSSubPropertyType) => item.propertyId === over.id
                )

                dispatch({
                    type: PPL_ACTIONS.setSubProperties,
                    payload: arrayMove(subProperties, oldItem, newItem),
                })
            }
        },
        [dispatch, subProperties]
    )

    const handlePropertyNameBlur = useCallback(() => {
        if (
            state.properties.some(
                (prop: NonMSPropertyType) =>
                    prop.propertyName === propertyName &&
                    prop.dummyPropertyId !==
                        state.editableProperty?.dummyPropertyId
            )
        ) {
            dispatch({
                type: PPL_ACTIONS.setErrors,
                payload: {
                    ...state.errors,
                    propertyName: {
                        error: ERROR_DUPLICATE_HEADER,
                        value: propertyName,
                    },
                },
            })

            return
        }

        dispatch({
            type: PPL_ACTIONS.setPropertyName,
            payload: propertyName,
        })
    }, [
        dispatch,
        propertyName,
        state.properties,
        state.editableProperty,
        state.errors,
    ])

    const handlePropertyNameChange = useCallback(
        (value = '') => {
            setPropertyName(value)

            if (
                state.properties.some(
                    (prop: NonMSPropertyType) =>
                        prop.propertyName?.toLowerCase() ===
                            value?.toLowerCase() &&
                        prop.dummyPropertyId !==
                            state.editableProperty?.dummyPropertyId
                )
            ) {
                dispatch({
                    type: PPL_ACTIONS.setErrors,
                    payload: {
                        ...state.errors,
                        propertyName: {
                            error: ERROR_DUPLICATE_HEADER,
                            value: value,
                        },
                    },
                })

                return
            }

            dispatch({
                type: PPL_ACTIONS.batchNonMSActions,
                payload: [
                    {
                        type: PPL_ACTIONS.setPropertyName,
                        payload: value,
                    },
                    {
                        type: PPL_ACTIONS.setErrors,
                        payload: {
                            ...state.errors,
                            propertyName: {},
                        },
                    },
                ],
            })
        },
        [state, dispatch, handlePropertyNameBlur]
    )

    const handleDeleteProperty = useCallback(
        (dummyPropertyId = ''): MouseEventHandler => (e) => {
            e.stopPropagation()
            if (
                !!checkAllDeleteableOptions(
                    properties.find(
                        (prop: NonMSPropertyType) =>
                            prop.dummyPropertyId === dummyPropertyId
                    )
                )
            ) {
                return NavPromptDialogContainer(
                    JSON.stringify({
                        title: TITLE_DISCARD_CHANGES,
                        preferredMessage: PROMPT_DISCARD_CHANGES,
                        buttonColor: buttonSecondary,
                        confirmText: 'Yes',
                        cancelText: 'No',
                    } as NavPromptMessageProps),
                    (callbackState) => {
                        if (callbackState) {
                            dispatch({
                                type: PPL_ACTIONS.batchNonMSActions,
                                payload: [
                                    {
                                        type: PPL_ACTIONS.setMode,
                                        payload: '',
                                    },
                                    {
                                        type: PPL_ACTIONS.setEditProperty,
                                        payload: {},
                                    },
                                    {
                                        type: PPL_ACTIONS.setEditPropertyID,
                                        payload: '',
                                    },
                                ],
                            })
                        }
                    }
                )
            }
        },
        [properties, dispatch]
    )

    const saveDisabled = useMemo(() => !!state.optionActive, [
        state.optionActive,
    ])

    const isPropertyGroupInline = useMemo(
        () =>
            !!property?.subProperties.some(
                (subProp) =>
                    !!subProp.optionGroups.some(
                        (optGrp) => !!optGrp.prefix || !!optGrp.postfix
                    )
            ) || state.mode === 'inline',
        [property, state.mode]
    )

    const handleAddProperty = useCallback(() => {
        if (!saveDisabled) {
            dispatch({ type: PPL_ACTIONS.insertProperty })
            setProcessInsert(true)
        }
    }, [dispatch, setProcessInsert, saveDisabled])

    const ChildRenderer = memo(
        ({
            index,
            subProp,
            activeId,
        }: {
            index?: number
            subProp?: NonMSSubPropertyType
            activeId?: string
        }) => {
            let subProperty = subProp
            let subPropertyIndex = index

            if (!subProperty && !subPropertyIndex && !!activeId) {
                subPropertyIndex = subProperties.findIndex(
                    (sub: NonMSSubPropertyType) => sub.propertyId === activeId
                )
                subProperty = subProperties[subPropertyIndex]
            }

            if (
                !subProperty ||
                subPropertyIndex === undefined ||
                subPropertyIndex === null
            ) {
                return <></>
            }

            return (
                <SortableItem
                    id={subProperty.propertyId}
                    handle={subProperties.length > 1}
                    disabled={subProperties.length < 2}
                    className={classes.subPropertyContainer}
                >
                    <GridContainer>
                        <NonMSEditableSubProperty
                            index={subPropertyIndex}
                            subProperty={subProperty}
                            isPropertyGroupInline={isPropertyGroupInline}
                        />
                    </GridContainer>
                </SortableItem>
            )
        }
    )

    useEffect(() => {
        // NOTE: This catches the errors before clicking the check button.
        if (
            !!processInsert &&
            (!!state.errors.propertyName || !!state.errors.subPropertyName)
        ) {
            setProcessInsert(false)
        }

        if (
            !!processInsert &&
            !state.errors.propertyName &&
            !state.errors.subPropertyName
        ) {
            dispatch({
                type: PPL_ACTIONS.batchNonMSActions,
                payload: [
                    { type: PPL_ACTIONS.setEditProperty, payload: {} },
                    {
                        type: PPL_ACTIONS.setEditPropertyID,
                        payload: '',
                    },
                    {
                        type: PPL_ACTIONS.setMode,
                        payload: '',
                    },
                ],
            })
            setProcessInsert(false)
        }
    }, [processInsert, state.errors, dispatch])

    useEffect(() => {
        if (!!property) {
            dispatch({
                type: PPL_ACTIONS.batchNonMSActions,
                payload: [
                    { type: PPL_ACTIONS.setEditProperty, payload: property },
                    { type: PPL_ACTIONS.resetErrors },
                ],
            })
        }

        // NOTE: this is used as componentDidMount and need not a dependency array
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    return (
        <GridContainer>
            <GridItem className={classes.tab2Container}>
                <Typography
                    component="span"
                    className={classes.nonMSBasicContainer}
                >
                    <Typography
                        component="span"
                        className={classes.propertyNameInputContainer}
                    >
                        <PropertyNameAutoComplete
                            defaultValue={propertyName}
                            type="header"
                            setAutocompleteValue={handlePropertyNameChange}
                            errors={state.errors.propertyName}
                        />
                    </Typography>
                    <SortableComponent
                        onDragEnd={handleDragEnd}
                        sortableItems={subProperties.map(
                            (item: NonMSSubPropertyType, index: number) =>
                                item.propertyId ?? index
                        )}
                        modifiers={[
                            restrictToVerticalAxis,
                            restrictToParentElement,
                        ]}
                        strategy={verticalListSortingStrategy}
                        ActiveComponent={ChildRenderer}
                    >
                        {subProperties.map(
                            (subProp: NonMSSubPropertyType, index: number) => (
                                <ChildRenderer
                                    subProp={subProp}
                                    index={index}
                                    key={`${subProp.propertyId}_${index}`}
                                />
                            )
                        )}
                    </SortableComponent>
                </Typography>
            </GridItem>

            {/* added test id to the buttons since there are no text to use as selector */}
            <GridItem className={classes.actionContainer}>
                <Typography
                    component="span"
                    className={clsx(classes.checkContainer, {
                        saveDisabled: !!saveDisabled,
                    })}
                    onClick={handleAddProperty}
                    role="button"
                    data-testid="addProperty"
                >
                    <CheckOutlined />
                </Typography>
                <DeleteButton
                    data-testid="btnCancelMoPropertyEdit"
                    onClick={handleDeleteProperty(
                        property?.dummyPropertyId ?? ''
                    )}
                />
            </GridItem>
        </GridContainer>
    )
}

export default NonMSEditableProperty
